import React, { useEffect, useMemo, useReducer, useState } from "react";
import BsModal from "../Common/BsModal";
import TextInput from "../Common/TextInput";
import SelectInput from "../Common/SelectInput";
import Notification from "../Common/Notification";
import { useAccounts } from "../../DataStore/AccountsContext";
import ajaxAccountChart from "../../util/remote/ajaxAccountChart";

/**
 * NewAccountModal
 * - Modal UI to create a new ledger account (Chart of Accounts)
 * - Opening balance removed
 * - Bank linking removed: replaced with a clear guideline panel
 * - Emphasizes: only Asset accounts under parent "Bank" (or "Cash & Bank") are linkable to banks
 */
const reducer = (state, action) => {
  if (action.type === "RESET") return { ...initialState };
  return { ...state, [action.type]: action.value };
};

const initialState = {
  loading: false,

  // core
  accountCategory: null,
  accountNumber: "",
  accountTitle: "",
  accountDescription: "",

  // structure
  isSubAccount: true,
  mainAccount: null,

  // ui
  showMore: false,
};

const NewAccountModal = ({
  show,
  onClose,
  onSave = () => {},
  buttonLabel = "Create Account",
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentNotify, setNotify] = useState(null);

  const { 
          accountCategoryList, 
          getAccountCategories,  
          getChartOfAccounts, 
          dispatch: accountDispatch
      } = useAccounts();

  useEffect(() => {
    if (show) {
      getAccountCategories();
    } else {
      setNotify(null);
    }
  }, [show]);


   useEffect(() => {
        if(state.accountCategory?.value){
          getCategoryAccounts();
        }
      }, [state.accountCategory]);
  

  useEffect(() => {
    if (!currentNotify) return;
    const t = setTimeout(() => setNotify(null), 5000);
    return () => clearTimeout(t);
  }, [currentNotify]);

  const categoryOptions = useMemo(() => {
    if (!Array.isArray(accountCategoryList)) return [];
    return accountCategoryList.map((cat) => ({
      label: cat.value + ' ' + cat.category_name, value: cat.category_id 
    }));
  }, [accountCategoryList]);

  // Dummy parent accounts for chosen category
  const parentAccountOptions = useMemo(() => {
    if(!state.accountListing || state.accountListing === '404') return [];
    return state.accountListing.map((acc) => ({ label: acc.account_number + ' ' + acc.account_name, value: acc.account_id }));
  }, [state.accountListing]);


  // Linkable rule (UI guide only):
  // - category must be Assets
  // - must be a sub-account
  // - parent must be "Bank" (dummy id 12)
  const looksLinkableToBank = useMemo(() => {
    const isAssets = Number(state.accountCategory?.value || 0) === 1;
    const isSub = !!state.isSubAccount;
    const parentIsBank = state.mainAccount?.value === 12;
    return isAssets && isSub && parentIsBank;
  }, [state.accountCategory, state.isSubAccount, state.mainAccount]);



  const getCategoryAccounts = async () => {
          dispatch({ type: 'accountListing', value: false });
          const server_response = await ajaxAccountChart.listAccountsForCategory(state.accountCategory.value);
          console.log('Category accounts response', server_response);
          if (server_response.status === 'OK') {
              dispatch({ type: 'accountListing', value: server_response.details });
          } else {
              dispatch({ type: 'accountListing', value: '404' });
          }
      };
  



  const requireField = (condition, message) => {
    if (condition) {
      setNotify(<Notification type="danger" message={message} />);
      return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (state.loading) return;

    if (requireField(!state.accountCategory?.value, "Select an account category.")) return;
    if (requireField(!String(state.accountNumber || "").trim(), "Enter an account number.")) return;
    if (requireField(!String(state.accountTitle || "").trim(), "Enter an account title.")) return;

    if (
      requireField(
        state.isSubAccount && !state.mainAccount?.value,
        "This is a sub-account. Please select a parent account."
      )
    )
      return;

    dispatch({ type: "loading", value: true });

    const payload = {
    account_number: state.accountNumber,
    account_name: state.accountTitle,
    description: state.accountDescription,
    category_id: state.accountCategory.value,
    main_account_id: state.isSubAccount ? state.mainAccount?.value : null,
    };


    dispatch({ type: 'loading', value: true });
    const server_response = await ajaxAccountChart.createAccount(payload);
    dispatch({ type: 'loading', value: false });

    if (server_response.status === 'OK') {
        setNotify(<Notification type="success" message={server_response.message} />);
        dispatch({ type: 'RESET' });
            /**
         * Refresh data in context after the success notification has showed up to mitigate the re-render effect.
         */
            setTimeout(() => {
            getChartOfAccounts(true);
        }, 2000);
    } else {
        setNotify(<Notification type="danger" message={server_response.message} />);
    }


  };

  const draftTitle = useMemo(() => {
    const n = String(state.accountNumber || "").trim();
    const t = String(state.accountTitle || "").trim();
    if (!n && !t) return "Draft account";
    if (n && !t) return `Draft: ${n}`;
    if (!n && t) return `Draft: ${t}`;
    return `Draft: ${n} • ${t}`;
  }, [state.accountNumber, state.accountTitle]);

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="New Ledger Account"
      size="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={state.loading}>
            Cancel
          </button>

          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={state.loading}>
            {state.loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle mr-1" /> {buttonLabel}
              </>
            )}
          </button>
        </>
      }
    >
      {/* Header strip (same vibe as your ManageBankModal) */}
      <div
        className="p-3 mb-3"
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="mr-3 d-flex align-items-center justify-content-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(40,167,69,0.12)",
                border: "1px solid rgba(40,167,69,0.25)",
              }}
            >
              <i className="fa fa-book text-success" />
            </div>

            <div>
              <div className="font-weight-bold" style={{ fontSize: 16 }}>
                {draftTitle}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {state.accountCategory?.label || "Choose a category"}
                {state.isSubAccount && state.mainAccount?.label ? ` • Parent: ${state.mainAccount.label}` : ""}
              </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => dispatch({ type: "showMore", value: !state.showMore })}
          >
            <i className={`fa ${state.showMore ? "fa-eye-slash" : "fa-info-circle"} mr-1`} />
            {state.showMore ? "Hide tips" : "Linking tips"}
          </button>
        </div>
      </div>

      {/* Linking guideline (no linking UI) */}
      {(state.showMore || canShowGuideline(state)) && (
        <BankLinkGuideline looksLinkableToBank={looksLinkableToBank} />
      )}

      {currentNotify}

      {/* Core inputs */}
      <div className="row">
        <div className="col-md-5">
          <TextInput
            type="number"
            label="Account Number"
            placeholder="e.g. 1010"
            leftIcon="fa fa-hashtag"
            value={state.accountNumber}
            onChange={(e) => dispatch({ type: "accountNumber", value: e.target.value })}
            required
            wrap
          />
        </div>

        <div className="col-md-7">
          <TextInput
            type="text"
            label="Account Title"
            placeholder="E.g. Administrative Expenses"
            leftIcon="fa fa-pen"
            value={state.accountTitle}
            onChange={(e) => dispatch({ type: "accountTitle", value: e.target.value })}
            required
            wrap
          />
        </div>
      </div>

      <TextInput
        type="textarea"
        label="Description"
        placeholder="What does this account track?"
        leftIcon="fa fa-align-left"
        value={state.accountDescription}
        onChange={(e) => dispatch({ type: "accountDescription", value: e.target.value })}
        wrap
      />

      <TextInput
        type="select"
        label="Account Category"
        placeholder={state.accountCategory?.label || "Select category"}
        iconClass="fa-regular fa-folder"
        options={categoryOptions}
        onChange={(item) => dispatch({ type: "accountCategory", value: item })}
        value={state.accountCategory}
        required
        wrap
      />

      {/* Structure card */}
      <div
        className="p-3 mt-2 mb-3"
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="font-weight-bold">
            <i className="fa fa-sitemap text-primary mr-2" />
            Account Structure
          </div>

          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id="isSubAccountSwitch"
              checked={state.isSubAccount}
              onChange={(e) => dispatch({ type: "isSubAccount", value: e.target.checked })}
            />
            <label className="custom-control-label" htmlFor="isSubAccountSwitch">
              Sub-account
            </label>
          </div>
        </div>

        <div className="text-muted mt-1" style={{ fontSize: 13 }}>
          Turn off “Sub-account” if this is a top-level (header) account.
        </div>

        {state.isSubAccount && (
          <div className="mt-3">
            <TextInput
              type="select"
              label="Parent Account"
              placeholder={state.mainAccount?.label || "Select parent account"}
              iconClass="fa fa-layer-group"
              options={parentAccountOptions}
              onChange={(item) => dispatch({ type: "mainAccount", value: item })}
              value={state.mainAccount}
              wrap
            />
          </div>
        )}
      </div>

      

      {/* helper: show guideline if user is in Assets or chose parent Bank, even if they didn't click "tips" */}
    </BsModal>
  );
};

// ------- tiny helper: decide when to auto-show guideline -------
const canShowGuideline = (state) => {
  const isAssets = state.accountCategory?.value * 1 === 3;
  return isAssets
};

// ------- guideline card component -------
const BankLinkGuideline = ({ looksLinkableToBank }) => {
  return (
    <div
      className="p-3 mb-2"
      style={{
        borderRadius: 12,
        border: "1px solid rgba(0,123,255,0.25)",
        background: "rgba(0,123,255,0.06)",
      }}
    >
      <div className="d-flex align-items-start">
        <div
          className="mr-3 d-flex align-items-center justify-content-center"
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: "rgba(0,123,255,0.12)",
            border: "1px solid rgba(0,123,255,0.25)",
            flex: "0 0 auto",
          }}
        >
          <i className="fa fa-university text-primary" />
        </div>

        <div style={{ flex: 1 }}>
          <div className="font-weight-bold" style={{ fontSize: 15 }}>
            How to link this account to a Bank
          </div>

          <div className="text-muted mt-1" style={{ fontSize: 13, lineHeight: 1.5 }}>
            Linking is done from <b>Banking → Bank Accounts</b>. Open the bank account you want, click{" "}
            <b>Manage</b>, then select the ledger account to link.
          </div>

          <div className="mt-2 text-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
            <b>Important:</b> Only accounts that meet both conditions can be linked to banks:
            <ul className="mb-0 mt-1 pl-3">
              <li>
                The account must be under <b>Assets</b>.
              </li>
              <li>
                The account must be a <b>Sub-account</b> with parent account set to <b>Bank</b>.
              </li>
            </ul>
          </div>

          <div className="mt-2">
            <span
              className={`badge ${looksLinkableToBank ? "badge-success" : "badge-secondary"}`}
              style={{ padding: "7px 10px", borderRadius: 999 }}
            >
              {looksLinkableToBank ? "This account will be linkable to a bank" : "This account is not linkable yet"}
            </span>

            {!looksLinkableToBank && (
              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: choose <b>Assets</b> and set parent account to <b>Bank</b> (as a sub-account) to enable linking.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAccountModal;
